import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Input,
  Label,
  MoneyInput,
  PageHeader,
  Select,
  SkeletonRows,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type {
  MembershipCatalog,
  PaymentAmounts,
  Role,
  ShowcaseCounterKey,
  ShowcaseCounters,
} from '@bia-auction/shared-schema';
import {
  membershipCatalogSchema,
  paymentAmountsSchema,
  ROLES,
  SHOWCASE_COUNTER_KEYS,
  showcaseCountersAdminSchema,
} from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { formatCount } from '../lib/format';
import { roleLabel } from '../lib/labels';

type Pkg = MembershipCatalog[number];

/** Dirty-state hint + gated Save (redesign-admin.md §8) — edits are local until saved. */
function DirtySave({
  dirty,
  busy,
  onSave,
}: {
  dirty: boolean;
  busy: boolean;
  onSave: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      {dirty && <span className="text-sm text-warning-tint-fg">{t('config.unsaved')}</span>}
      <Button onClick={onSave} disabled={!dirty || busy}>
        {t('common.save')}
      </Button>
    </div>
  );
}

function PaymentAmountsCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['config', 'payment-amounts'],
    queryFn: () => api('/admin/config/payment-amounts', {}, paymentAmountsSchema),
  });
  const [deposit, setDeposit] = useState<number | null>(null);
  useEffect(() => {
    if (q.data) setDeposit(q.data.deposit);
  }, [q.data]);
  const dirty = q.data != null && deposit !== q.data.deposit;

  const save = useMutation({
    mutationFn: () =>
      api<PaymentAmounts>('/admin/config/payment-amounts', {
        method: 'PUT',
        body: { deposit: deposit ?? 0 },
      }),
    onSuccess: () => {
      toast(t('config.deposit.saved'), 'success');
      void qc.invalidateQueries({ queryKey: ['config', 'payment-amounts'] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('config.deposit.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <SkeletonRows rows={1} />
        ) : q.isError ? (
          <ErrorMsg error={q.error} />
        ) : (
          <div className="flex items-end justify-between gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfg-deposit">{t('config.deposit.label')}</Label>
              <MoneyInput id="cfg-deposit" className="w-40" value={deposit} onValueChange={setDeposit} />
            </div>
            <DirtySave dirty={dirty} busy={save.isPending} onSave={() => save.mutate()} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MembershipsCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['config', 'memberships'],
    queryFn: () => api('/admin/config/memberships', {}, membershipCatalogSchema),
  });
  const [rows, setRows] = useState<Pkg[]>([]);
  const [toDelete, setToDelete] = useState<number | null>(null);
  useEffect(() => {
    if (q.data) setRows(q.data);
  }, [q.data]);
  const dirty = q.data != null && JSON.stringify(rows) !== JSON.stringify(q.data);

  const save = useMutation({
    mutationFn: () => api<MembershipCatalog>('/admin/config/memberships', { method: 'PUT', body: rows }),
    onSuccess: () => {
      toast(t('config.memberships.saved'), 'success');
      void qc.invalidateQueries({ queryKey: ['config', 'memberships'] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error'),
  });

  const update = (i: number, patch: Partial<Pkg>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>{t('config.memberships.title')}</CardTitle>
        <DirtySave dirty={dirty} busy={save.isPending} onSave={() => save.mutate()} />
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <SkeletonRows rows={3} />
        ) : q.isError ? (
          <ErrorMsg error={q.error} />
        ) : (
          <>
            {/* One header row for the whole editor (was per-row micro-labels). */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2 font-medium">{t('config.col.slug')}</th>
                  <th className="p-2 font-medium">{t('config.col.name')}</th>
                  <th className="p-2 font-medium">{t('config.col.price')}</th>
                  <th className="p-2 font-medium">{t('config.col.role')}</th>
                  <th className="p-2 font-medium">{t('config.col.active')}</th>
                  <th className="w-10 p-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b align-middle">
                    <td className="p-2">
                      <Input aria-label={t('config.col.slug')} value={r.id} onChange={(e) => update(i, { id: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input aria-label={t('config.col.name')} value={r.name} onChange={(e) => update(i, { name: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <MoneyInput
                        aria-label={t('config.col.price')}
                        className="w-32"
                        value={r.amount}
                        onValueChange={(k) => update(i, { amount: k ?? 0 })}
                      />
                    </td>
                    <td className="p-2">
                      <Select
                        aria-label={t('config.col.role')}
                        value={r.role}
                        onChange={(e) => update(i, { role: e.target.value as Role })}
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {roleLabel(role)}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="p-2">
                      <Checkbox
                        aria-label={t('config.col.active')}
                        checked={r.active}
                        onCheckedChange={(v) => update(i, { active: v === true })}
                      />
                    </td>
                    <td className="p-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('config.removePackage')}
                        onClick={() => setToDelete(i)}
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setRows((rs) => [...rs, { id: '', name: '', amount: 0, role: 'GOLDBIDDER', active: true }])
              }
            >
              {t('config.addPackage')}
            </Button>
          </>
        )}
      </CardContent>

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={t('config.removePackage')}
        description={t('config.removePackageConfirm')}
        confirmLabel={t('config.removePackage')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          setRows((rs) => rs.filter((_, idx) => idx !== toDelete));
          setToDelete(null);
        }}
      />
    </Card>
  );
}

/**
 * Showcase marketing counters (M16). Each row is one counter: show it or not, and the
 * baseline added to the real figure.
 *
 * The row shows **real, baseline and the resulting public figure side by side**, live as you
 * type. The whole point of the feature is that an operator chooses what a stranger sees, and
 * they cannot choose well against a number they have to compute in their head — Özcan's
 * *"they shouldn't see 4000 auctions and 3 cars sold"* is a judgement about how a pair of
 * figures reads together, so both have to be on screen while the judgement is made.
 */
function ShowcaseCountersCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['config', 'showcase-counters'],
    queryFn: () => api('/admin/config/showcase-counters', {}, showcaseCountersAdminSchema),
  });
  const [draft, setDraft] = useState<ShowcaseCounters | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.counters);
  }, [q.data]);
  const dirty = q.data != null && draft != null && !isSameCounters(draft, q.data.counters);

  const save = useMutation({
    mutationFn: () =>
      api('/admin/config/showcase-counters', { method: 'PUT', body: draft }, showcaseCountersAdminSchema),
    onSuccess: () => {
      toast(t('config.counters.saved'), 'success');
      void qc.invalidateQueries({ queryKey: ['config', 'showcase-counters'] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error'),
  });

  const patch = (key: ShowcaseCounterKey, next: Partial<ShowcaseCounters[ShowcaseCounterKey]>) =>
    setDraft((d) => (d ? { ...d, [key]: { ...d[key], ...next } } : d));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>{t('config.counters.title')}</CardTitle>
        <DirtySave dirty={dirty} busy={save.isPending} onSave={() => save.mutate()} />
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('config.counters.intro')}</p>
        {q.isLoading ? (
          <SkeletonRows rows={2} />
        ) : q.isError ? (
          <ErrorMsg error={q.error} />
        ) : draft && q.data ? (
          <>
            <div className="grid grid-cols-[1fr_auto_10rem_6rem_8rem] items-end gap-3 text-sm text-muted-foreground">
              <span>{t('config.counters.col.counter')}</span>
              <span>{t('config.counters.col.show')}</span>
              <span>{t('config.counters.col.baseline')}</span>
              <span className="text-right">{t('config.counters.col.real')}</span>
              <span className="text-right">{t('config.counters.col.shown')}</span>
            </div>
            {SHOWCASE_COUNTER_KEYS.map((key) => {
              const row = draft[key];
              const real = q.data.real[key];
              return (
                <div key={key} className="grid grid-cols-[1fr_auto_10rem_6rem_8rem] items-center gap-3">
                  <Label htmlFor={`cnt-${key}`}>{t(`config.counters.${key}`)}</Label>
                  <Checkbox
                    id={`cnt-${key}`}
                    checked={row.enabled}
                    onCheckedChange={(v) => patch(key, { enabled: v === true })}
                  />
                  <Input
                    type="number"
                    min={0}
                    aria-label={`${t(`config.counters.${key}`)} — ${t('config.counters.col.baseline')}`}
                    value={String(row.baseline)}
                    onChange={(e) => patch(key, { baseline: Math.max(0, Number(e.target.value) || 0) })}
                  />
                  <span className="text-right tabular-nums text-muted-foreground">
                    {formatCount(real)}
                  </span>
                  {/* The figure a visitor gets — greyed out when the counter is off, because
                      the sum is still arithmetically true but nobody will ever see it. */}
                  <span
                    className={`text-right font-semibold tabular-nums ${
                      row.enabled ? '' : 'text-muted-foreground line-through'
                    }`}
                  >
                    {formatCount(row.baseline + real)}
                  </span>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground">
              {SHOWCASE_COUNTER_KEYS.every((k) => !draft[k].enabled)
                ? t('config.counters.allOff')
                : t('config.counters.hiddenNote')}
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Field-wise compare — the draft is a fresh object every keystroke, so identity won't do. */
function isSameCounters(a: ShowcaseCounters, b: ShowcaseCounters): boolean {
  return SHOWCASE_COUNTER_KEYS.every(
    (k) => a[k].enabled === b[k].enabled && a[k].baseline === b[k].baseline,
  );
}

export function ConfigPage() {
  return (
    <div className="space-y-4">
      <PageHeader title={t('config.title')} />
      <PaymentAmountsCard />
      <MembershipsCard />
      <ShowcaseCountersCard />
    </div>
  );
}
